import { BookOpen, GraduationCap, Landmark, Link, Mail, ShoppingCart } from 'lucide-react'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { displayAssociationTime, displayDateTime } from '@/lib/dates'
import { connectionPath, unmatchedPath, type Connection } from './types'

const icons = {
  woocommerce: ShoppingCart,
  moodle: GraduationCap,
  xero: Landmark,
  mailchimp: Mail,
  quickbooks_online: BookOpen,
}
const states: Record<string, { label: string; variant: BadgeProps['variant'] }> = {
  healthy: { label: 'Healthy', variant: 'success' },
  failing: { label: 'Failing', variant: 'warning' },
  needs_attention: { label: 'Needs attention', variant: 'destructive' },
  paused: { label: 'Paused', variant: 'neutral' },
  removed: { label: 'Removed', variant: 'neutral' },
  unavailable: { label: 'Unavailable', variant: 'neutral' },
}
export function HealthBadge({ row }: { row: Connection }) {
  const state = states[row.status === 'active' ? row.health : row.status]
  return <Badge variant={state.variant}>{state.label}</Badge>
}
export function ConnectionName({ row }: { row: Connection }) {
  const Icon = icons[row.connection_type as keyof typeof icons] ?? Link
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-flex size-6 items-center justify-center rounded-sm bg-primary/10 text-primary"
        aria-hidden="true"
      >
        <Icon className="size-3.5" strokeWidth={1.5} />
      </span>
      <a
        className="whitespace-nowrap font-medium leading-snug text-foreground"
        href={connectionPath(row)}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {row.display_name}
      </a>
    </div>
  )
}
export function relativeConnectionTime(value: string) {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(value).getTime()) / 60000))
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
  const days = Math.round(hours / 24)
  return `${days} ${days === 1 ? 'day' : 'days'} ago`
}
const outcomes: Record<string, string> = {
  running: 'Running',
  failed: 'Failed',
  partial: 'Partial',
  paused: 'Paused',
  abandoned: 'Abandoned',
}
export function LastRun({ row }: { row: Connection }) {
  if (!row.last_run_at) return <span className="text-muted-foreground">Never run</span>
  return (
    <div className="leading-snug">
      <time dateTime={row.last_run_at} title={displayDateTime(row.last_run_at)}>
        {relativeConnectionTime(row.last_run_at)}
      </time>
      {row.last_run_status && outcomes[row.last_run_status] && (
        <div className="text-caption text-muted-foreground">{outcomes[row.last_run_status]}</div>
      )}
    </div>
  )
}
export function NextRun({ row }: { row: Connection }) {
  if (row.next_run_at)
    return (
      <time
        className="text-muted-foreground"
        dateTime={row.next_run_at}
        title={displayDateTime(row.next_run_at)}
      >
        {displayAssociationTime(row.next_run_at)}
      </time>
    )
  const halted = row.status === 'active' && row.health === 'needs_attention'
  return <span className="text-muted-foreground">{halted ? 'Halted' : '—'}</span>
}
export function ConnectionCount({ row, kind }: { row: Connection; kind: 'unmatched' | 'error' }) {
  const count = row[`${kind}_count`]
  if (!count) return <span className="text-muted-foreground">0</span>
  const path =
    kind === 'unmatched'
      ? `${unmatchedPath}?connection_id=${row.id}`
      : `${connectionPath(row)}#errors`
  return (
    <Button asChild variant="link" size="inline">
      <a
        href={path}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {count}
      </a>
    </Button>
  )
}
